// ═══ 검차정산 데스크톱(≥1024) 본문 — 시안 C1 D (신고 371 §8 결정 5·9·10) ═══
// 순수 표시 컴포넌트다 — 조회·필터 상태·재집계는 모두 SimpleFees.js 가 갖고 여기로 내려준다.
// 시안 대비 판단 2건: ① 사이드 카드 제목은 simpleB1InspectorPill('평가사') —
// simpleB1InspectorAll('전체 평가사') 은 필터 해제 행으로 써야 해서 제목과 겹친다.
// ② 안내문 앞 info 아이콘은 생략(신규 자산 0).
import React from 'react';
import { T, R, S, FONT } from '../../../theme';
import { useT, useLocale } from '../../../../i18n/I18nProvider';
import { fmtDate } from '../../fmt';
import SimpleCard from '../../parts/SimpleCard';
import SideCard from '../../parts/SideCard';
import MoneyKRW from '../../parts/MoneyKRW';
import LtrText from '../../parts/LtrText';
import SimpleStatusPill from '../../parts/SimpleStatusPill';

var tiles = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 };
var tilePad = '12px 16px';
var tileLabel = { fontSize: FONT.label, fontWeight: 700, color: T.muted };
var tileValue = { display: 'block', fontSize: 20, fontWeight: 800, color: T.ink, letterSpacing: '-.4px', marginTop: 4 };
var tileMoney = Object.assign({}, tileValue, { color: T.money });
var tileAccentCard = { background: T.accentTint, border: '1px solid ' + T.accentBd };
var tileAccentLabel = Object.assign({}, tileLabel, { color: T.accentPress });
var tileAccentValue = Object.assign({}, tileValue, { color: T.accentPress });

var chips = { marginInlineStart: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6 };
var chipCount = { fontWeight: 800, fontVariantNumeric: 'tabular-nums' };

var table = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
var th = { textAlign: 'start', padding: '8px 14px', fontSize: 11, fontWeight: 700, color: T.muted,
  borderBottom: '1px solid ' + T.bd, whiteSpace: 'nowrap' };
var thRight = Object.assign({}, th, { textAlign: 'end' });
var td = { height: 34, padding: '0 14px', borderBottom: '1px solid ' + T.bdSoft, color: T.body, whiteSpace: 'nowrap' };
var tdCar = Object.assign({}, td, { fontWeight: 700, color: T.ink, whiteSpace: 'normal' });
var tdRight = Object.assign({}, td, { textAlign: 'end', fontWeight: 700 });
var tfootTd = { height: 36, padding: '0 14px', background: T.head, fontSize: 12.5, fontWeight: 800, color: T.ink };
var tfootRight = Object.assign({}, tfootTd, { textAlign: 'end' });

var insRow = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', textAlign: 'start',
  borderInlineStart: '3px solid transparent', borderBottom: '1px solid ' + T.bdSoft, borderTop: 0, borderInlineEnd: 0,
  background: 'none', cursor: 'pointer', fontFamily: 'inherit' };
var insRowOn = Object.assign({}, insRow, { borderInlineStart: '3px solid ' + T.accent, background: T.accentTint });
var av = { width: 26, height: 26, borderRadius: 7, flex: 'none', display: 'grid', placeItems: 'center',
  background: T.track, color: T.dim, fontSize: 11, fontWeight: 800 };
var avOn = Object.assign({}, av, { background: T.accent, color: T.card });
var insName = { flex: 1, minWidth: 0 };
var insTitle = { display: 'block', fontSize: 12.5, fontWeight: 700, color: T.ink, overflow: 'hidden',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var insSub = { display: 'block', fontSize: 11, color: T.muted, marginTop: 1 };
var insAmount = { fontSize: 12.5, fontWeight: 800, color: T.money, flex: 'none' };

var sideCol = { display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 };
var note = { padding: '10px 12px', borderRadius: R.card, border: '1px solid ' + T.bd, background: T.card,
  fontSize: 11.5, lineHeight: 1.55, color: T.sub };

// 상태 칩 — 시안 D 는 상태 원문 라벨(검차완료/매입결정/매입불가)을 쓴다(모바일 시트의 '결정 대기' 와 다름).
var STATUS_CHIPS = [[null, 'evalFilterAll'], ['completed', 'evalStatusCompleted'],
  ['decided', 'evalStatusDecided'], ['rejected', 'evalStatusRejected']];

function carTitleOf(it) {
  var parts = [(it && it.car_model) || '-'];
  if (it && it.car_detail) parts.push(it.car_detail);
  return parts.join(' ');
}

function initialOf(name) {
  var s = String(name || '').trim();
  return s ? s.slice(0, 1) : '-';
}

function Tile(props) {
  return (<SimpleCard pad={tilePad} style={props.accent ? tileAccentCard : undefined}>
    <span style={props.accent ? tileAccentLabel : tileLabel}>{props.label}</span>
    {props.children}
  </SimpleCard>);
}

function FeesDesktop(props) {
  var t = useT();
  var loc = useLocale();
  var items = props.items || [];
  var byInspector = props.byInspector || [];
  var counts = props.statusCounts || {};
  var allCount = byInspector.reduce(function (s, e) { return s + Number(e.count || 0); }, 0);
  var allTotal = byInspector.reduce(function (s, e) { return s + Number(e.total || 0); }, 0);

  return (<React.Fragment>
    <div style={tiles} data-testid="ss-fees-tiles">
      <Tile label={t('simpleFeeCountLabel')}>
        <span style={tileValue}>{t('simpleInspectionsCount', { count: props.count })}</span>
      </Tile>
      <Tile label={t('simpleFeeTotal')}>
        <MoneyKRW won={props.total} style={tileMoney} />
      </Tile>
      <Tile label={t('simpleB1InspectorPill')}>
        <LtrText style={tileValue}>{byInspector.length}</LtrText>
      </Tile>
      {props.unitFee ? (<Tile label={t('simpleFeeLabel')} accent>
        <MoneyKRW won={props.unitFee} style={tileAccentValue} />
      </Tile>) : null}
    </div>

    <div className="ss-side">
      <SimpleCard title={t('simpleFeesTitle')} right={props.isEmptyMonth ? null : (<span style={chips}>
        {STATUS_CHIPS.map(function (opt) {
          var on = props.selectedStatus === opt[0];
          return (<button key={opt[0] || 'all'} type="button" data-testid="ss-fees-chip"
            style={on ? S.chipOn : S.chip}
            onClick={function () { props.onStatus(opt[0]); }}>
            {t(opt[1])}<b style={chipCount}>{counts[opt[0] || 'all'] || 0}</b>
          </button>);
        })}
      </span>)}>
        {props.isEmptyMonth ? (<div style={S.emptyText}>{t('simpleFeeEmpty')}</div>) : (<table style={table}>
          <thead>
            <tr>
              <th style={th}>{t('evalTableColCar')}</th>
              <th style={th}>{t('evalFormCarNumber')}</th>
              <th style={th}>{t('evalTableColDate')}</th>
              <th style={th}>{t('evalTableColInspector')}</th>
              <th style={th}>{t('evalTableColStatus')}</th>
              <th style={thRight}>{t('simpleFeeLabel')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(function (it) {
              return (<tr key={it.id} data-testid="ss-fees-row">
                <td style={tdCar}>{carTitleOf(it)}</td>
                <td style={td}><LtrText>{it.car_number || '-'}</LtrText></td>
                <td style={td}><LtrText>{fmtDate(loc.locale, it.inspect_date) || '-'}</LtrText></td>
                <td style={td}>{it.inspector_name || '-'}</td>
                <td style={td}><SimpleStatusPill status={it.status} /></td>
                <td style={tdRight}><MoneyKRW won={it.fee} /></td>
              </tr>);
            })}
          </tbody>
          <tfoot>
            <tr data-testid="ss-fees-total">
              <td style={tfootTd} colSpan={5}>{t('simpleFeeTotal')}</td>
              <td style={tfootRight}><MoneyKRW won={props.total} /></td>
            </tr>
          </tfoot>
        </table>)}
      </SimpleCard>

      <div className="ss-sticky" style={sideCol}>
        <SideCard title={t('simpleB1InspectorPill')} count={byInspector.length}>
          <button type="button" data-testid="ss-fees-side-row"
            style={props.selectedInspector == null ? insRowOn : insRow}
            onClick={function () { props.onInspector(null); }}>
            <span style={props.selectedInspector == null ? avOn : av} aria-hidden="true">
              {initialOf(t('simpleB1InspectorAll'))}
            </span>
            <span style={insName}>
              <b style={insTitle}>{t('simpleB1InspectorAll')}</b>
              <span style={insSub}>{t('simpleInspectionsCount', { count: allCount })}</span>
            </span>
            <MoneyKRW won={allTotal} style={insAmount} />
          </button>
          {byInspector.map(function (e) {
            var on = props.selectedInspector === e.inspector_id;
            return (<button key={e.inspector_id} type="button" data-testid="ss-fees-side-row"
              style={on ? insRowOn : insRow}
              onClick={function () { props.onInspector(e.inspector_id); }}>
              <span style={on ? avOn : av} aria-hidden="true">{initialOf(e.inspector_name)}</span>
              <span style={insName}>
                <b style={insTitle}>{e.inspector_name || '-'}</b>
                <span style={insSub}>{t('simpleInspectionsCount', { count: e.count })}</span>
              </span>
              <MoneyKRW won={e.total} style={insAmount} />
            </button>);
          })}
        </SideCard>
        <div style={note}>{t('simpleFeeNote')}</div>
      </div>
    </div>
  </React.Fragment>);
}

export default FeesDesktop;
